import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import AppHeader from 'tio-employee/components/app/header';
import AuthRegisterPostAuthTermsDialog from 'tio-employee/components/auth/register/post-auth-terms-dialog';
import AuthSessionExpiringDialog from 'tio-employee/components/auth/session-expiring-dialog';
import ImpersonationBanner from 'tio-employee/components/impersonation-banner';
import Component from '@glimmer/component';
import SidebarUserMenu from 'tio-employee/components/app/profile/sidebar-user-menu';
import type { TOC } from '@ember/component/template-only';
import type EmployeeService from 'tio-employee/services/employee';
import type PartnerService from 'tio-employee/services/partner';
import type { RoleViewExtension as RoleViewModel } from 'tio-data/data/resources/role-view.ext';
import type SessionContextService from 'tio-employee/services/session-context';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type { Store as EmployeeStoreService } from '@warp-drive/core';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';
import { HStack } from 'tio-ui/components/layout';
import { and, or } from 'tio-ui/utilities';
import type FeaturesService from 'tio-common/services/features';
import type SideDrawerService from 'tio-common/services/side-drawer';
import type ZendeskService from 'tio-employee/services/zendesk';
import type Owner from '@ember/owner';
import { getOwner } from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import { Sidebar, SidebarItem, SidebarItemGroup } from 'tio-common/components/sidebar';
import {
  PhBookOpen,
  PhCurrencyCircleDollar,
  PhChatCircleDots,
  PhDetective,
  PhGlobe,
  PhGraduationCap,
  PhGridNine,
  PhHouseLine,
  PhHandCoins,
  PhHandHeart,
  PhQuestionMark,
  PhLightbulbFilament,
  PhScroll,
  PhRocketLaunch,
  PhTrayArrowUp,
  PhUsersThree,
  PhUserCircle,
  PhStudent,
  PhX,
} from 'ember-phosphor-icons';
import { LinkTo } from '@ember/routing';
import Divider from 'tio-ui/components/divider';

interface AuthenticatedRouteSignature {
  Args: {
    model: {
      user: UserModel;
      wellnessData: Record<string, unknown> | null;
    };
  };
  Blocks: {
    default: [];
  };
}

export default class AuthenticatedRouteComponent extends Component<AuthenticatedRouteSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare demoMode: DemoModeService;
  @service declare store: EmployeeStoreService;
  @service declare partner: PartnerService;
  @service declare employee: EmployeeService;
  @service declare features: FeaturesService;
  @service declare sideDrawer: SideDrawerService;
  @service declare router: RouterService;
  @service declare zendesk: ZendeskService;

  @tracked currentRole?: RoleViewModel;
  @tracked isSidebarOpen = true;

  constructor(owner: Owner, args: AuthenticatedRouteSignature['Args']) {
    super(owner, args);
    this.sideDrawer.isOpen = false;
    this.currentRole = this.sessionContext.currentRole;
  }

  get isSidebarOpenHack() {
    // Bridge until old one is gone.
    // This is how the hamburger stack is able to open the sidebar.
    return this.sideDrawer.isOpen;
  }

  setCurrentRole = (role: RoleViewModel) => {
    this.sessionContext.setCurrentRole(role);
    this.currentRole = role;
  };

  get taPartnerPaymentsReviewPageEnabled() {
    return this.features.isEnabled('taPartnerPaymentsReviewPage');
  }

  get isTaOnly() {
    return this.partner.isTaOnly || this.employee.isTaOnlyEmployee;
  }

  get hasTuitionAssistance() {
    return !!this.partner.hasNewTuitionAssistance;
  }

  get isOwner() {
    return !!this.sessionContext.roles.find((e) => e.relationshipType === 'ACCOUNT_OWNER');
  }

  get isAdmin() {
    return !!this.sessionContext.roles.find((e) => e.relationshipType === 'TIO_ADMIN');
  }

  get isParticipant() {
    return !!(
      this.sessionContext.roles.find((e) => e.relationshipType === 'PARTICIPANT') && !this.isTaOnly
    );
  }

  get isFamilyMember() {
    return this.sessionContext.isFamilyMember;
  }

  get isEmployeeAdmin() {
    return !!this.sessionContext.roles.find((e) => e.relationshipType === 'EMPLOYEE_ADMIN');
  }

  get isPslfAdmin() {
    return !!this.sessionContext.roles.find((e) => e.relationshipType === 'PSLF_ADMIN');
  }

  get isPslfEnabled() {
    return !!this.partner.isPslfEnabled && !this.employee.hasRogueFein;
  }

  get isSyfEnabled() {
    return !!this.partner.isSyfEnabled;
  }

  get idrEnabled() {
    return this.partner.isIdrEnabled;
  }

  get isTasApprover() {
    return !!this.sessionContext.roles.find((e) => e.relationshipType.startsWith('TAS.Approver'));
  }

  get showTuitionAssistance() {
    // Backend taEnabled already checks for launched program templates
    return !!this.partner.company?.taEnabled;
  }

  get showSlr() {
    // check for contribution plan
    return this.employee.current.plan && this.employee.current.plan.cadence !== 'NEVER';
  }

  get useReimaginedAccountActivity() {
    return false;
  }

  get showReimaginedToggle() {
    return false;
  }

  toggleReimaginedView = () => {};

  get hasD5Train() {
    const train = this.args.model.wellnessData;
    return train?.wellnessTrain === 'D-5';
  }

  get showWellness() {
    return !this.hasD5Train;
  }

  get isLpnEnabled() {
    const { lpn } = this.partner.companySettings;
    if (lpn && 'isEnabled' in lpn) {
      return lpn.isEnabled as boolean;
    }
    return false;
  }

  get isApprovalPage() {
    return this.router.currentRouteName === 'authenticated.admin.tas.applications.approval';
  }

  get isFullWidthPage() {
    // Check if the current route declares a full-width layout requirement
    const routeName = this.router.currentRouteName;
    if (!routeName) return false;

    try {
      // Use official Ember API to look up route instance
      const owner = getOwner(this);
      const routeInstance = owner?.lookup(`route:${routeName}`) as
        { layoutType?: string } | undefined;
      return routeInstance?.layoutType === 'full-width';
    } catch {
      return false;
    }
  }

  get mainClasses() {
    // Remove max-width constraint for full-width pages to allow sidebar to extend to viewport edge
    return this.isFullWidthPage ? 'scroll-smooth' : 'scroll-smooth max-w-7xl';
  }

  get needsToAcceptTerms() {
    // Suppress the terms dialog ONLY for the fabricated demo identity — the
    // `/me` sentinel that has no live backend record. It cannot meaningfully
    // accept terms, and its "I Agree" would POST the sentinel id ("demo-user
    // is not a valid value for id"). This is the defense-in-depth backstop
    // against the gate misfiring on a stale/live register-conditions
    // comparison (the fixture also ships accepted agreements + an empty
    // conditions fixture, so the underlying gate is normally satisfied).
    //
    // Gate on isDemoSessionUser, NOT isActive/isArmed: those are true for a
    // REAL logged-in user on a demo-flagged path too (the sticky flag is
    // path-scoped), and a real user who genuinely hasn't accepted terms must
    // still see the dialog. isDemoSessionUser keys on the loaded identity, so
    // it also covers the paused state without over-suppressing.
    if (this.demoMode.isDemoSessionUser) {
      return false;
    }
    return this.sessionContext.needsToAcceptAppTerms;
  }

  didCompleteTerms = async () => {
    // Re-fetch /me with all includes so agreement relationships
    // (usageAgreement, privacyAgreement, customRegisterAgreements)
    // are refreshed and needsToAcceptAppTerms flips to false
    await this.sessionContext.load();
  };

  get isZendeskEnabled() {
    return this.partner.isZendeskEnabled;
  }

  openChat = () => {
    this.zendesk.openWebChatWindow();
  };

  toggleSidebar = () => {
    this.sideDrawer.toggleDrawer();
  };

  <template>
    <ImpersonationBanner />
    <div class="min-h-screen antialiased tio-bg-sunken text-black">
      {{! Most examples use display none with a time, Removing pointer events
          allows css to just do its thing.
      }}
      <div
        class="relative z-50 lg:hidden {{unless this.isSidebarOpenHack 'pointer-events-none'}} "
        role="dialog"
        aria-modal="true"
      >
        <div
          class="fixed inset-0 transition-opacity ease-linear duration-300 bg-black/20
            {{if this.isSidebarOpenHack 'opacity-100' 'opacity-0'}}
            "
        ></div>

        <div class="fixed inset-0 flex">
          <div
            class="relative mr-16 flex w-full max-w-xs flex-1 transition-transform ease-in-out duration-300
              {{if this.isSidebarOpenHack 'translate-x-0' '-translate-x-full'}}
              "
          >
            <div
              class="absolute left-full top-0 flex w-8 h-16 bg-midnight rounded-r-md mt-5 transition-opacity ease-linear duration-300
                {{if this.isSidebarOpenHack 'opacity-100' 'opacity-0'}}
                "
            >
              <button type="button" class="w-8 h-16" {{on "click" this.toggleSidebar}}>
                <span class="sr-only">{{t "common.sidebar.close"}}</span>
                <PhX @size="24" class="tio-fg-inverse" />
              </button>
            </div>

            <div class="tio-col-flush grow gap-y-5 overflow-y-auto bg-midnight px-6 pb-4">
              <Sidebar>
                <SidebarContent
                  @isTaOnly={{this.isTaOnly}}
                  @showTuitionAssistance={{this.showTuitionAssistance}}
                  @isParticipant={{this.isParticipant}}
                  @isFamilyMember={{this.isFamilyMember}}
                  @showPslf={{this.isPslfEnabled}}
                  @showSyf={{this.isSyfEnabled}}
                  @showIdr={{this.idrEnabled}}
                  @showWellness={{this.showWellness}}
                  @isPslfAdmin={{this.isPslfAdmin}}
                  @isTasApprover={{this.isTasApprover}}
                  @isTasPartnerPaymentsEnabled={{this.taPartnerPaymentsReviewPageEnabled}}
                  @isEmployeeAdmin={{this.isEmployeeAdmin}}
                  @isAdmin={{this.isAdmin}}
                  @isOwner={{this.isOwner}}
                  @showSlr={{this.showSlr}}
                  @useReimaginedAccountActivity={{this.useReimaginedAccountActivity}}
                  @showReimaginedToggle={{this.showReimaginedToggle}}
                  @onToggleReimaginedView={{this.toggleReimaginedView}}
                  @isLpnEnabled={{this.isLpnEnabled}}
                  @isZendeskEnabled={{this.isZendeskEnabled}}
                  @onOpenChat={{this.openChat}}
                />
              </Sidebar>
            </div>
          </div>
        </div>
      </div>

      {{! Static sidebar for desktop }}
      <div data-legacy-sidebar class="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-56 lg:flex-col">
        <div class="tio-col grow overflow-y-auto border-r tio-border-default bg-midnight px-6 pb-4">
          <Sidebar>
            <SidebarContent
              @isTaOnly={{this.isTaOnly}}
              @showTuitionAssistance={{this.showTuitionAssistance}}
              @isParticipant={{this.isParticipant}}
              @isFamilyMember={{this.isFamilyMember}}
              @showPslf={{this.isPslfEnabled}}
              @showSyf={{this.isSyfEnabled}}
              @showWellness={{this.showWellness}}
              @showIdr={{this.idrEnabled}}
              @isPslfAdmin={{this.isPslfAdmin}}
              @isTasApprover={{this.isTasApprover}}
              @isTasPartnerPaymentsEnabled={{this.taPartnerPaymentsReviewPageEnabled}}
              @isEmployeeAdmin={{this.isEmployeeAdmin}}
              @isAdmin={{this.isAdmin}}
              @isOwner={{this.isOwner}}
              @showSlr={{this.showSlr}}
              @useReimaginedAccountActivity={{this.useReimaginedAccountActivity}}
              @showReimaginedToggle={{this.showReimaginedToggle}}
              @onToggleReimaginedView={{this.toggleReimaginedView}}
              @isLpnEnabled={{this.isLpnEnabled}}
              @isZendeskEnabled={{this.isZendeskEnabled}}
              @onOpenChat={{this.openChat}}
            />
          </Sidebar>
        </div>
      </div>

      <div class="lg:pl-56">
        <div data-legacy-banner>
          <AppHeader />
        </div>
        <main class={{this.mainClasses}}>
          <div class="px-0">
            {{outlet}}
          </div>
        </main>
      </div>
    </div>

    {{#if this.needsToAcceptTerms}}
      <AuthRegisterPostAuthTermsDialog
        @primaryEmailAddress={{this.sessionContext.user.primaryEmail}}
        @didSave={{this.didCompleteTerms}}
      />
    {{/if}}

    {{! Showing status handled by dialog component itself }}
    <AuthSessionExpiringDialog />
  </template>
}

interface SidebarContentSignature {
  Args: {
    isTaOnly: boolean;
    showTuitionAssistance: boolean;
    isParticipant: boolean;
    isFamilyMember: boolean;
    showPslf: boolean;
    showSyf: boolean;
    showIdr: boolean;
    showSlr: boolean;
    useReimaginedAccountActivity: boolean;
    showReimaginedToggle: boolean;
    onToggleReimaginedView: () => void;
    showWellness: boolean;
    isAdmin: boolean;
    isOwner: boolean;
    isPslfAdmin: boolean;
    isTasApprover: boolean;
    isTasPartnerPaymentsEnabled: boolean;
    isEmployeeAdmin: boolean;
    isLpnEnabled: boolean;
    isZendeskEnabled: boolean;
    onOpenChat: () => void;
  };
}

const SidebarContent: TOC<SidebarContentSignature> = <template>
  <li>
    <ul role="list" class="tio-col-extra-tight -mx-2">
      <SidebarItem
        @route="authenticated.dashboard"
        @icon={{PhHouseLine}}
        @label={{t "sidebar.home"}}
      />
      {{#if (or @isAdmin @isOwner @isPslfAdmin @isTasApprover @isEmployeeAdmin)}}
        <div class="tio-text-xs-semibold leading-6 text-indigo-200 mt-6!">
          {{t "sidebar.administration"}}
        </div>
      {{/if}}
      {{#if (or @isAdmin @isOwner)}}
        <SidebarItem
          @route="authenticated.admin.reports"
          @icon={{PhGridNine}}
          @label={{t "common.reporting.default"}}
          data-test-sidebar-admin-reports
        />
        <SidebarItem
          @route="authenticated.admin.employees"
          @icon={{PhUsersThree}}
          @label={{t "sidebar.employees"}}
          data-test-sidebar-admin-employees
        />
        <SidebarItem
          @route="authenticated.admin.eligibility-files"
          @icon={{PhTrayArrowUp}}
          @label={{t "sidebar.eligibility_files"}}
          data-test-sidebar-admin-eligibility-files
        />
      {{/if}}
      {{#if (or @isAdmin (and @isOwner @showPslf) @isPslfAdmin)}}
        <SidebarItem
          @route="authenticated.admin.pslf.forms"
          @icon={{PhHandHeart}}
          @label={{t "sidebar.admin_pslf"}}
          data-test-sidebar-admin-pslf
        />
      {{/if}}
      {{#if (or @isAdmin @isOwner @isTasApprover)}}
        <SidebarItemGroup @icon={{PhStudent}} @label={{t "sidebar.admin_tuition_assistance"}}>
          {{#if (and @isTasPartnerPaymentsEnabled (or @isAdmin @isOwner @isEmployeeAdmin))}}
            <SidebarItem
              @route="authenticated.admin.tuition-assistance.payments"
              @label={{t "sidebar.payments"}}
              data-test-sidebar-admin-tuition-assistance-payments
            />
          {{/if}}
          <SidebarItem
            @route="authenticated.admin.tas.approval-queue"
            @label={{t "sidebar.approval_queue"}}
            data-test-sidebar-admin-tas-approval-queue
          />
        </SidebarItemGroup>
      {{/if}}
      {{! TODO: Eventually this should be a single link that goes to reporting index page with links to each page }}
      {{#if (or @isAdmin @isOwner @isEmployeeAdmin)}}
        <SidebarItem
          @route="authenticated.admin.reporting.index"
          @label={{t "reports.all_reports_link"}}
          data-test-sidebar-admin-tuition-assistance-reports
        />
      {{/if}}
      <div class="tio-text-xs-semibold leading-6 text-indigo-200 mt-6!">
        {{t "sidebar.your_benefits"}}
      </div>
      {{#if (and @isTaOnly @showTuitionAssistance)}}
        <SidebarItem
          @route="authenticated.tas.dashboard"
          @icon={{PhGraduationCap}}
          @label={{t "sidebar.tuition_assistance"}}
          data-test-sidebar-employee-tuition-assistance-dashboard
        />
        {{#if @isLpnEnabled}}
          <SidebarItem
            @route="authenticated.lpn.index"
            @label={{t "sidebar.learning_partners"}}
            @icon={{PhGlobe}}
          />
        {{/if}}
      {{/if}}

      {{#if (or @isParticipant @isFamilyMember)}}
        {{#if @showSlr}}
          <SidebarItemGroup @icon={{PhUserCircle}} @label={{t "sidebar.account_activity"}}>
            <SidebarItem
              @route="authenticated.account-activity.accounts"
              @label={{t "sidebar.loan_summary"}}
              data-test-sidebar-employee-student-loan-summary
            />
            <SidebarItem
              @route="authenticated.slr.dashboard"
              @label={{t "sidebar.employer_contributions"}}
              data-test-sidebar-employee-employer-contributions
            />
          </SidebarItemGroup>
        {{else}}
          <SidebarItem
            @route="authenticated.account-activity.accounts"
            @icon={{PhUserCircle}}
            @label={{t "sidebar.loan_summary"}}
            data-test-sidebar-employee-student-loan-summary
          />
        {{/if}}
        {{#unless @isFamilyMember}}
          {{#if @showPslf}}
            <SidebarItem
              @route="authenticated.pslf.dashboard"
              @icon={{PhHandCoins}}
              @label={{t "sidebar.loan_forgiveness"}}
              data-test-sidebar-pslf
            />
          {{/if}}
          {{#if @showTuitionAssistance}}
            <SidebarItem
              @route="authenticated.tas.dashboard"
              @icon={{PhGraduationCap}}
              @label={{t "sidebar.tuition_assistance"}}
              data-test-sidebar-employee-primary-tuition-assistance
            />
          {{/if}}
          {{#if @showSyf}}
            <SidebarItem
              @route="authenticated.syf.dashboard"
              @icon={{PhRocketLaunch}}
              @label={{t "sidebar.secure_your_future"}}
              data-test-sidebar-employee-primary-secure-your-future
            />
          {{/if}}
          {{#if @showIdr}}
            <SidebarItem
              @icon={{PhCurrencyCircleDollar}}
              @route="authenticated.idr.dashboard"
              @label={{t "sidebar.income_driven_repayment"}}
              data-test-sidebar-employee-primary-income-driven-repayment
            />
          {{/if}}
          {{#if @showWellness}}
            <SidebarItem
              @route="authenticated.wellness-insights"
              @icon={{PhLightbulbFilament}}
              @label={{t "sidebar.loan_insights"}}
              data-test-sidebar-loan-insights
            />
          {{/if}}
        {{/unless}}

        <SidebarItemGroup
          @icon={{PhCurrencyCircleDollar}}
          @label={{t "sidebar.repaying_debt"}}
          data-test-sidebar-employee-primary-repaying-student-debt
        >
          <SidebarItem
            @route="authenticated.repaying-student-debt.coaching"
            @label={{t "sidebar.loan_coaches"}}
          />
          {{#unless @isFamilyMember}}
            <SidebarItem
              @route="authenticated.repaying-student-debt.loan-forgiveness.index"
              @label={{t "sidebar.loan_forgiveness"}}
            />
          {{/unless}}
          <SidebarItem
            @route="authenticated.repaying-student-debt.repayment-strategy-finder"
            @label={{t "sidebar.strategy_finder"}}
          />
          {{#unless @isFamilyMember}}
            <SidebarItem
              @route="authenticated.repaying-student-debt.repayment-options"
              @label={{t "sidebar.consolidation_refinancing"}}
            />
          {{/unless}}
        </SidebarItemGroup>
        {{#if @isLpnEnabled}}
          <SidebarItem
            @route="authenticated.lpn.index"
            @label={{t "sidebar.learning_partners"}}
            @icon={{PhGlobe}}
          />
        {{/if}}
        {{#unless @isFamilyMember}}
          {{#unless @showTuitionAssistance}}
            <SidebarItem
              @route="authenticated.college-planning-mentors"
              @label={{t "sidebar.education_mentors"}}
              @icon={{PhUsersThree}}
            />
          {{/unless}}

          <div class="tio-text-xs-semibold leading-6 text-indigo-200 mt-6!">
            {{t "sidebar.resources"}}
          </div>
          <SidebarItemGroup @icon={{PhBookOpen}} @label={{t "sidebar.questions"}}>
            <SidebarItem
              @route="authenticated.questions-you-have.paying-for-college"
              @label={{t "sidebar.types_of_student_loans"}}
            />
            <SidebarItem
              @route="authenticated.questions-you-have.repayment-plans"
              @label={{t "sidebar.repayment_plans"}}
            />
            <SidebarItem
              @route="authenticated.questions-you-have.glossary"
              @label={{t "sidebar.loan_glossary"}}
            />
          </SidebarItemGroup>
        {{/unless}}
      {{/if}}
    </ul>
  </li>

  <li class="mt-auto">
    <Divider />
    <div class="tio-col-flush my-2 tio-text-sm-medium tio-fg-inverse">
      <a
        href="https://www.tuition.io/privacy-policy"
        class="tio-flex-extra-tight w-full group -mx-2 rounded-md p-2 tio-text-sm-semibold tio-fg-inverse text-left hover:tio-bg-sunken hover:text-midnight no-underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        <PhDetective
          @size="20"
          class="tio-icon-md [&.active]:text-midnight [&.active]:font-bold shrink-0 hover:text-midnight"
        />
        {{t "sidebar.privacy_policy"}}
      </a>
      <a
        href="https://www.tuition.io/terms-of-service"
        class="tio-flex-extra-tight w-full group -mx-2 rounded-md p-2 tio-text-sm-semibold tio-fg-inverse text-left hover:tio-bg-sunken hover:text-midnight no-underline"
        target="_blank"
        rel="noopener noreferrer"
      ><PhScroll
          @size="20"
          class="tio-icon-md [&.active]:text-midnight [&.active]:font-bold shrink-0 hover:text-midnight"
        />
        {{t "sidebar.terms_of_service"}}
      </a>
    </div>
  </li>
  <li>
    <div class="tio-col-flush tio-text-sm-medium tio-fg-inverse">
      <ul>
        <SidebarUserMenu />
        <Divider />
        {{! Shows pinning to bottom of sidebar }}
        <HStack>
          <div class="flex justify-around mt-4">
            {{#if @isZendeskEnabled}}
              <button
                type="button"
                class="tio-stack-center cursor-pointer"
                {{on "click" @onOpenChat}}
              >
                <PhChatCircleDots @size="16" />
                {{t "sidebar.support"}}
              </button>
            {{else}}
              <LinkTo @route="authenticated.contact-us" class="tio-stack-center">
                <PhChatCircleDots @size="16" />
                {{t "sidebar.support"}}
              </LinkTo>
            {{/if}}
            <LinkTo @route="authenticated.contact-us" class="tio-stack-center">
              <PhQuestionMark @size="16" />
              {{t "sidebar.help"}}
            </LinkTo>
          </div>
        </HStack>
      </ul>
    </div>
  </li>
</template>;
